// Layer 4 (UI) — the shared downtime band (locked comp
// `.impeccable/mocks/decision/downtime.html`, variant A): the conversion strip
// (lost mill hours → feed tonnes → US$ → what one availability point is worth a
// year), then the largest causes by US$ beside monthly mill availability with
// that point drawn as a band on its axis. Every figure comes from
// DowntimeImpact.transform.ts; nothing is typed here.

import type { ReactNode } from 'react';
import { Button, GlassCard, Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { cn } from '@/shared/cn';
import { formatCurrency } from '@/versions/v1/modules/capacity/Scene.transform';
import { ActorChip } from '../advisory/PipelineStrip';
import { formatHours } from '../maintenance/Downtime.transform';
import type { CauseCategoryId } from '../maintenance/downtime.seed';
import { CARD_TABLE, DowntimeCard, HOT_ROW, RankBar } from './DowntimeCard';
import {
  DOWNTIME_WINDOWS,
  millAvailabilityBand,
  type AvailabilityBandRead,
  type DowntimeImpactRead,
  type DowntimeWindowId,
  uptimeOpportunityRead,
} from './DowntimeImpact.transform';
import { CURRENCY_GLYPH } from '@/versions/v1/shared/money';

type DowntimeImpactProps = {
  read: DowntimeImpactRead;
  yearToDateRead: DowntimeImpactRead;
  onOpenCause: (tab: 'pareto' | 'value', causeCategory: CauseCategoryId) => void;
};

/** The cause rows' two drill buttons: a hairline outline so they read as
 *  actions beside the figures, not as more muted text. */
const CAUSE_LINK = 'h-6 whitespace-nowrap border border-[var(--hairline-strong)] px-2 text-xs';

function formatTonnes(tonnes: number): string {
  return `${Math.round(tonnes).toLocaleString('en-US')} t`;
}

function formatPct(value: number): string {
  return `${value.toFixed(1)}%`;
}

function formatRate(value: number): string {
  return value.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** The window buttons in the page header (comp `.seg.sm`): one pill group,
 *  the chosen window filled in ink. */
export function DowntimeWindowSegment({
  windowId,
  onWindowChange,
}: {
  windowId: DowntimeWindowId;
  onWindowChange: (windowId: DowntimeWindowId) => void;
}) {
  return (
    <div
      role="group"
      aria-label="Downtime window"
      className="inline-flex items-center gap-0.5 rounded-full border border-[var(--hairline-strong)] bg-[var(--surface-1)] p-[3px]"
    >
      {DOWNTIME_WINDOWS.map((window) => (
        <button
          key={window.id}
          type="button"
          aria-pressed={window.id === windowId}
          onClick={() => onWindowChange(window.id)}
          className={cn(
            'rounded-full px-2.5 py-1 text-xs font-medium transition-colors duration-150',
            window.id === windowId
              ? 'bg-ink-primary text-[var(--surface-2)]'
              : 'text-ink-secondary hover:bg-[var(--surface-2)] hover:text-ink-primary',
          )}
        >
          {window.label}
        </button>
      ))}
    </div>
  );
}

/** One cell of the conversion strip (DESIGN.md § Strip and stepper): caps
 *  stage name, primary figure, its comparison, actor chips pinned to the foot;
 *  a rotated notch on each inner boundary carries the direction of travel. */
export function StripCell({
  name,
  figure,
  unit,
  children,
  actors,
  year = false,
  notch = true,
}: {
  name: string;
  figure: string;
  /** A per-unit suffix set small on the figure's baseline (`/dst`). */
  unit?: string;
  children: ReactNode;
  actors?: ReactNode;
  year?: boolean;
  notch?: boolean;
}) {
  const face = year
    ? 'bg-[color-mix(in_srgb,var(--state-online)_10%,var(--surface-2))]'
    : 'bg-[var(--surface-2)]';
  return (
    <div
      data-strip-cell={name}
      className={cn(
        // K18 r1: pr-1.5 gives the wider US$ comparison lines their pre-D26 single
        // line (`7.4% of US$4,869,743 scheduled-period EBITDA`, `ONE AVAILABILITY
        // POINT · US$/YR`) without moving the left inset or unwrapping the line
        // above them at 1280 (pr-1 did the latter).
        'relative flex min-h-[7.5rem] min-w-0 flex-col gap-1 border-b border-r border-[var(--hairline)] py-3 pl-4 pr-1.5 last:border-r-0 xl:border-b-0',
        face,
        notch
          ? "after:pointer-events-none after:absolute after:right-[-6px] after:top-1/2 after:z-10 after:hidden after:h-[10px] after:w-[10px] after:-translate-y-1/2 after:rotate-45 after:border-r after:border-t after:border-[var(--hairline-strong)] after:bg-[var(--surface-2)] after:content-[''] xl:after:block"
          : '',
      )}
    >
      <span className="text-xs font-semibold uppercase tracking-[0.07em] text-muted">{name}</span>
      <span className="text-2xl font-semibold leading-none tracking-[-0.01em] tabular-nums text-ink-primary">
        {figure}
        {unit ? <span className="text-sm font-semibold tracking-normal text-ink-secondary">{unit}</span> : null}
      </span>
      {children}
      {actors ? <span className="mt-auto flex min-w-0 flex-wrap gap-1 pt-1">{actors}</span> : null}
    </div>
  );
}

/** A comparison line: the lead term in 600 ink-secondary, the rest muted. */
export function Comparison({ lead, children }: { lead?: string; children?: ReactNode }) {
  return (
    <span className="text-xs leading-snug tabular-nums text-muted">
      {lead ? <span className="font-semibold text-ink-secondary">{lead}</span> : null}
      {children}
    </span>
  );
}

/** Monthly mill availability as bars against the Year 1 plan, with one
 *  availability point drawn as a band from the plan up and priced on the axis
 *  (D12). Hand-drawn SVG: `TrendChart` has no band or per-bar state tone. */
function AvailabilityBandChart({ band }: { band: AvailabilityBandRead }) {
  const width = 360;
  const height = 150;
  const pad = { l: 34, r: 8, t: 16, b: 34 };
  const [lo, hi] = band.domain;
  const y = (value: number) => pad.t + ((height - pad.t - pad.b) * (hi - value)) / (hi - lo);
  const barW = 46;
  const gap = (width - pad.l - pad.r - barW * band.months.length) / band.months.length;
  const bandLabel = `${band.bandHours.toLocaleString('en-US')} h · ${formatCurrency(band.bandAnnualA)}/yr`;
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      role="img"
      aria-label={`Mill availability by month against the ${formatPct(band.planPct)} plan; one point (${formatPct(band.bandLowPct)}–${formatPct(band.bandHighPct)}) is ${bandLabel}`}
      className="block overflow-visible"
    >
      <rect
        data-availability-band
        x={pad.l}
        y={y(band.bandHighPct)}
        width={width - pad.l - pad.r}
        height={y(band.bandLowPct) - y(band.bandHighPct)}
        fill="color-mix(in srgb, var(--state-online) 12%, transparent)"
      />
      {band.ticks.map((tick) => (
        <g key={tick}>
          <line x1={pad.l} x2={width - pad.r} y1={y(tick)} y2={y(tick)} stroke="var(--hairline-strong)" />
          <text x={pad.l - 5} y={y(tick) + 3} textAnchor="end" fontSize="10" fill="var(--ink-tertiary)" className="tabular-nums">
            {tick}%
          </text>
        </g>
      ))}
      <text x={pad.l + 5} y={y((band.bandLowPct + band.bandHighPct) / 2) + 3.5} fontSize="10" fontWeight="600" fill="var(--state-online)" className="tabular-nums">
        {bandLabel}
      </text>
      {band.months.map((month, index) => {
        const x = pad.l + gap / 2 + index * (barW + gap);
        return (
          <g key={month.id}>
            <rect
              x={x}
              y={y(month.availabilityPct)}
              width={barW}
              height={Math.max(0, y(lo) - y(month.availabilityPct))}
              rx="3"
              fill={month.atPlan ? 'var(--state-online)' : 'var(--state-warning)'}
              opacity="0.9"
            />
            <text
              x={x + barW / 2}
              y={y(month.availabilityPct) - 5}
              textAnchor="middle"
              fontSize="11"
              fontWeight="600"
              fill="var(--ink-primary)"
              stroke="var(--surface-2)"
              strokeWidth="4"
              paintOrder="stroke"
              className="tabular-nums"
            >
              {formatPct(month.availabilityPct)}
            </text>
            <text x={x + barW / 2} y={height - 18} textAnchor="middle" fontSize="11" fontWeight="600" fill="var(--ink-primary)">
              {month.code}
            </text>
            <text x={x + barW / 2} y={height - 6} textAnchor="middle" fontSize="10" fill="var(--ink-tertiary)">
              {month.monthLabel}
            </text>
          </g>
        );
      })}
      <line
        x1={pad.l}
        x2={width - pad.r}
        y1={y(band.planPct)}
        y2={y(band.planPct)}
        stroke="var(--ink-primary)"
        strokeWidth="1.3"
        strokeDasharray="4 3"
      />
    </svg>
  );
}

export function DowntimeImpactBand({ read, yearToDateRead, onOpenCause }: DowntimeImpactProps) {
  const uptime = uptimeOpportunityRead();
  const band = millAvailabilityBand(read.monthlyAvailability, uptime);
  const ebitdaPerHour = `${CURRENCY_GLYPH}${formatRate(uptime.currentEbitdaPerHourA)}/h`;
  const topThreeShare = read.topCauses.reduce((total, cause) => total + cause.sharePct, 0);
  const pts = band.latestVsPlanPts;

  return (
    <section id="walkthrough-downtime-impact" aria-label="Downtime impact" className="space-y-3">
      <GlassCard
        tone="matte"
        aria-label="Hours to money"
        className="overflow-hidden rounded-2xl border-[var(--hairline-strong)] p-0"
      >
        <div className="grid grid-cols-2 xl:grid-cols-4">
          <StripCell
            name="Lost mill hours · after buffer"
            figure={formatHours(read.lostHours)}
            actors={<ActorChip actor="System">Event log</ActorChip>}
          >
            <Comparison lead={formatPct(read.availabilityPct)}> mill availability of {formatHours(read.scheduledHours)} scheduled</Comparison>
          </StripCell>
          <StripCell name="Lost feed tonnes" figure={formatTonnes(read.lostTonnes)}>
            <Comparison lead={formatPct(read.lostPctOfPlan)}>
              {' '}of {formatTonnes(read.plannedTonnes)} at the {read.planRateTonnesPerHour.toFixed(1)} t/h operating rate
            </Comparison>
          </StripCell>
          <StripCell
            name={`Lost value · ${CURRENCY_GLYPH}`}
            figure={formatCurrency(read.valueLostA)}
            actors={<ActorChip actor="Engine">Engine · {ebitdaPerHour}</ActorChip>}
          >
            <Comparison lead={formatPct(read.lostPctOfEbitda)}> of {formatCurrency(read.periodEbitdaA)} scheduled-period EBITDA</Comparison>
          </StripCell>
          <StripCell name={`One availability point · ${CURRENCY_GLYPH}/yr`} figure={formatCurrency(uptime.annualEbitdaA)} year notch={false}>
            <Comparison lead={`${uptime.additionalHours.toLocaleString('en-US')} h`}>
              {' '}of {uptime.yearOneScheduledHours.toLocaleString('en-US')} h × {ebitdaPerHour} EBITDA
            </Comparison>
            <Comparison>
              Year 1 to date:{' '}
              <span className="font-semibold text-ink-secondary">
                {formatHours(yearToDateRead.lostHours)} · {formatCurrency(yearToDateRead.valueLostA)}
              </span>{' '}
              lost
            </Comparison>
          </StripCell>
        </div>
      </GlassCard>

      <div className="grid min-w-0 gap-3 xl:grid-cols-12 xl:items-start">
        <DowntimeCard
          title={`Largest causes by ${CURRENCY_GLYPH}`}
          meta={`Top ${read.topCauses.length} · ${formatPct(topThreeShare)} of ${formatCurrency(read.valueLostA)} total`}
          className="xl:col-span-8"
        >
          <div className={CARD_TABLE}>
            <Table wrap={false} className="tabular-nums">
              <THead>
                <TR>
                  <TH className="w-8">#</TH>
                  <TH>Cause</TH>
                  <TH className="w-[38%]">Share of lost value</TH>
                  <TH className="text-right">{CURRENCY_GLYPH} lost</TH>
                  <TH className="text-right">Drill down</TH>
                </TR>
              </THead>
              <TBody>
                {read.topCauses.map((cause, index) => (
                  <TR key={cause.causeCategory} className={index === 0 ? HOT_ROW : undefined}>
                    <TD className={cn('font-semibold', index === 0 ? '!text-[var(--color-accent-500)]' : '!text-muted')}>{index + 1}</TD>
                    <TD className="whitespace-nowrap font-medium !text-ink-primary">{cause.label}</TD>
                    <TD>
                      <span className="flex items-center gap-2.5">
                        <RankBar className="flex-1" ratio={cause.sharePct / 100} tone={index === 0 ? 'top' : 'ink'} />
                        <span className="w-11 text-right text-xs text-muted">{formatPct(cause.sharePct)}</span>
                      </span>
                    </TD>
                    <TD className="whitespace-nowrap text-right font-semibold !text-ink-primary">{formatCurrency(cause.dollars)}</TD>
                    <TD className="whitespace-nowrap text-right">
                      <span className="inline-flex gap-1">
                        <Button
                          variant="ghost"
                          className={CAUSE_LINK}
                          aria-label={`Open ${cause.label} in Pareto`}
                          onClick={() => onOpenCause('pareto', cause.causeCategory)}
                        >
                          Pareto
                        </Button>
                        <Button
                          variant="ghost"
                          className={CAUSE_LINK}
                          aria-label={`Open ${cause.label} in Value lost`}
                          onClick={() => onOpenCause('value', cause.causeCategory)}
                        >
                          Value lost
                        </Button>
                      </span>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </div>
        </DowntimeCard>

        <DowntimeCard
          title="Mill availability by month"
          meta={`vs ${formatPct(band.planPct)} Year 1 plan`}
          className="xl:col-span-4"
          aria-label="Monthly mill availability"
        >
          <AvailabilityBandChart band={band} />
          <p className="mt-1 flex justify-between gap-2 text-xs text-muted">
            <span>dashed = {formatPct(band.planPct)} plan · band = {band.bandPts} pt</span>
            <span className="tabular-nums">
              {band.latest.code}{' '}
              <span className={cn('font-semibold', pts >= 0 ? 'text-[var(--state-online)]' : 'text-[var(--state-warning)]')}>
                {pts >= 0 ? '+' : '−'}{Math.abs(pts).toFixed(1)} pts
              </span>{' '}
              {pts >= 0 ? 'over' : 'under'} plan
            </span>
          </p>
        </DowntimeCard>
      </div>
    </section>
  );
}
